import api from "@/app/api";
import type { ProductionDutyRateDateRange } from "@/app/services/generated/dutyService";
import { dutyService } from "@/app/services/generated/dutyService";
import type {
  AlcoholDutyLineRequirements,
  PackageTypeDutyRequirements,
  StockLocationsDutyRequirements,
} from "@/app/services/generated/dutyrulesService";
import {
  AlcoholDutyHeaderRequirements,
  dutyRulesService,
} from "@/app/services/generated/dutyrulesService";
import type { EnumAlcoholType } from "@/app/services/generated/enums/enumAlcoholType.generated";
import type { EnumStockAdjustDutyImplication } from "@/app/services/generated/enums/enumStockAdjustDutyImplication.generated";
import userStore from "@/store/modules/userStore";
import { reactive, readonly, watch } from "vue";

interface DutyConfigurationStoreState {
  headerRequirements: AlcoholDutyHeaderRequirements;
  allowedAlcoholTypes: EnumAlcoholType[];
  stockAdjustmentDutyImplications: EnumStockAdjustDutyImplication[];
  alcoholTypeToRequirementsMap: Map<
    EnumAlcoholType,
    AlcoholDutyLineRequirements
  >;
  stockLocationDutyRequirements: StockLocationsDutyRequirements | null;
  packageTypeDutyRequirements: PackageTypeDutyRequirements | null;
  allProductionDutyDateRanges: readonly ProductionDutyRateDateRange[];
}

const state = reactive<DutyConfigurationStoreState>({
  headerRequirements: new AlcoholDutyHeaderRequirements(),
  allowedAlcoholTypes: [],
  stockAdjustmentDutyImplications: [],
  alcoholTypeToRequirementsMap: new Map(),
  stockLocationDutyRequirements: null,
  packageTypeDutyRequirements: null,
  allProductionDutyDateRanges: [],
});

const refreshStore = async () => {
  [
    state.headerRequirements,
    state.allowedAlcoholTypes,
    state.stockLocationDutyRequirements,
    state.stockAdjustmentDutyImplications,
    state.packageTypeDutyRequirements,
    state.allProductionDutyDateRanges,
  ] = await Promise.all([
    dutyRulesService.getAlcoholDutyHeaderRequirements(),
    dutyRulesService.getAllowedAlcoholTypes(),
    dutyRulesService.getStockLocationDutyRequirements(),
    (await dutyRulesService.getStockAdjustmentDutyRequirements())
      .allowedDutyImplications,
    dutyRulesService.getPackageTypeDutyRequirements(),
    dutyService.getAllProductionDutyDateRanges(api.tenantId),
  ]);

  const dutyLineRequirements =
    await dutyRulesService.getAlcoholDutyLineRequirementsForTypes(
      state.allowedAlcoholTypes
    );

  dutyLineRequirements.forEach((requirement) => {
    state.alcoholTypeToRequirementsMap.set(
      requirement.alcoholType,
      requirement
    );
  });
};

watch(
  () => userStore.state.tenant,
  async () => {
    if (userStore.state.tenant) {
      await refreshStore();
    }
  },
  { immediate: true }
);

export default {
  state: readonly(state),
  refreshStore,
};
